import {
	Component,
	HostListener,
	Inject,
	Injector,
	OnInit,
	runInInjectionContext,
	OnDestroy,
	effect,
	signal,
	computed
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { NgHttpCachingService } from 'ng-http-caching';
import { Subject } from 'rxjs';

import { AppUpdateService, AppUpdateState, PageTrackingService } from '@shure/cloud/device-management/shared/services';
import { monitorLoginState, OktaInterfaceService, oktaSessionGuard } from '@shure/cloud/shared/okta/data-access';
import { RealTimeInAppBrowserNotificationService } from '@shure/cloud/shared/services/websocket';
import { MixpanelAnalyticsProvider } from '@shure/cloud/shared/utils/analytics';
import { APP_ENVIRONMENT, AppEnvironment } from '@shure/cloud/shared/utils/config';
import { CookiebotService } from '@shure/cloud/shared/utils/cookiebot';
import { CloudFeatureFlagsService, FEATURE_FLAG_SERVICE } from '@shure/cloud/shared/utils/feature-flags';
import { PrismDialogService, PrismSnackbarService } from '@shure/prism-angular-components';
import { PrismInfoDialogComponent, PrismInfoDialogData } from '@shure/shared/angular/ui/components/dialogs/info';

import { environment } from '../environments/environment';

@Component({
	selector: 'sh-app-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
	public appVersion: string;
	public clientVersion: string | undefined;
	private loggedIn = signal(false);
	// TODO: Is this used anywhere or can it be removed?
	private destroy = new Subject<boolean>();
	public readonly appUpdateStateEnum = AppUpdateState;

	private trackMixpanelEvents = toSignal(this.featureFlagsService.getFeatureFlag$('cdm-track-mixpanel-events'));
	private shouldInitMixpanel = computed(
		() =>
			this.cookiebotService.isCookiebotReady() &&
			!this.mixpanelAnalyticsProvider.isMixpanelInitialized() &&
			this.mixpanelAnalyticsProvider.isAnalyticsOptedIn() &&
			this.trackMixpanelEvents()
	);

	constructor(
		private metaTagService: Meta,
		public oktaIntf: OktaInterfaceService,
		@Inject(APP_ENVIRONMENT) private appEnv: AppEnvironment,
		private injector: Injector,
		private ngHttpCachingService: NgHttpCachingService,
		public readonly appUpdateService: AppUpdateService,
		private readonly dialogService: PrismDialogService,
		private readonly snackbarService: PrismSnackbarService,
		private readonly transloco: TranslocoService,
		private readonly mixpanelAnalyticsProvider: MixpanelAnalyticsProvider,
		private readonly cookiebotService: CookiebotService,
		@Inject(FEATURE_FLAG_SERVICE) private readonly featureFlagsService: CloudFeatureFlagsService,
		private readonly pageTrackingService: PageTrackingService, // injected here to start service
		private realTimeInAppBrowserNotificationService: RealTimeInAppBrowserNotificationService
	) {
		this.appVersion = this.appEnv.appVersion;
		this.clientVersion = this.appEnv.clientVersion;

		monitorLoginState(oktaIntf, {
			onLogIn: this.onLogIn.bind(this),
			onLogOut: this.onLogOut.bind(this)
		});

		this.appUpdateService.onUpdateComplete.subscribe((isReloadRequired) => {
			if (isReloadRequired) {
				this.showReloadDialog();
			} else {
				this.showReloadSnackbar();
			}
		});

		// Initialize Mixpanel when Cookiebot is ready, user is opted in, feature flag is true, and Mixpanel is not already initialized
		effect(() => {
			if (this.shouldInitMixpanel()) {
				this.initMixPanel();
			}
		});

		// Send Sign In event when Mixpanel is initialized and user is logged in
		effect(() => {
			if (this.mixpanelAnalyticsProvider.isMixpanelInitialized() && this.loggedIn()) {
				this.mixpanelAnalyticsProvider.sendSignInEvent();
			}
		});
	}

	//
	// The purpose of this code is to handle the case when a user has both
	// Shure Cloud tabs and CDM tabs opened, and then logs out of the Shure Cloud.
	// When the user clicks back into the CDM window, we'll check to see if the
	// session still exists, and if not logout the user.
	//
	// window:focus events are triggered only the first time the user clicks
	// in a window after having the cursor focused elsewhere.
	//
	@HostListener('window:focus', ['$event'])
	public onFocus(_event: FocusEvent): void {
		// the oktaSessionGuard must run in an injection context, which is why this
		// code isn't a simple function call.
		runInInjectionContext<void>(this.injector, () =>
			oktaSessionGuard(<ActivatedRouteSnapshot>{}, <RouterStateSnapshot>{})
		);
	}

	public ngOnInit(): void {
		if (environment.production) {
			this.metaTagService.addTags([{ name: 'robots', content: 'index' }]);
		} else {
			this.metaTagService.addTags([{ name: 'robots', content: 'noindex, nofollow' }]);
		}
		this.cookiebotService.addCookiebotListeners();
	}

	public ngOnDestroy(): void {
		this.cookiebotService.removeCookiebotListeners();
		this.realTimeInAppBrowserNotificationService.disconnectWebSocket();
	}

	private onLogIn(): void {
		this.destroy = new Subject();
		this.ngHttpCachingService.clearCache();
		this.loggedIn.set(true);
	}

	private onLogOut(): void {
		this.destroy.next(true);
		this.destroy.complete();

		if (this.loggedIn()) {
			this.loggedIn.set(false);
			window.location.reload();
		}
	}

	private showReloadDialog(): void {
		this.dialogService
			.openDialog<PrismInfoDialogComponent, PrismInfoDialogData, unknown>(PrismInfoDialogComponent, {
				id: 'sh-reload-required-dialog',
				data: {
					title: this.transloco.translate('cloud.device-management.app-update.new-features-available'),
					body: [this.transloco.translate('cloud.device-management.app-update.reload-required-message')],
					okLabel: this.transloco.translate('cloud.device-management.app-update.refresh')
				}
			})
			.afterClosed$.subscribe(() => {
				window.location.reload();
			});
	}

	private showReloadSnackbar(): void {
		this.snackbarService
			.open({
				duration: 0,
				message: this.transloco.translate('cloud.device-management.app-update.new-features-available'),
				actionText: this.transloco.translate('cloud.device-management.app-update.refresh')
			})
			.afterDismissed$.subscribe((dismissal) => {
				if (dismissal.dismissedByAction) {
					window.location.reload();
				}
			});
	}

	private initMixPanel(): void {
		this.mixpanelAnalyticsProvider.initMixPanel(
			'Cloud Device Management',
			this.appVersion,
			this.appEnv.mixpanelProjectToken
		);
	}
}
